import { useQuery } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { Handle, MarkerType, type Edge, type Node, type NodeProps, Position } from "@xyflow/react"
import { ArrowUpRight, Loader2, Plus, Shapes } from "lucide-react"
import { useMemo, useState } from "react"
import { refusalOfError } from "@/lib/api"
import { useT } from "@/lib/i18n"
import { layeredLayout } from "@/lib/layout"
import { type Expansion, NODE_BOUND, type Neighborhood, type ObjectIdentity, addPage, nodeId, setExpansion, start } from "@/lib/neighborhood"
import { useLinkPager } from "@/lib/queries"
import { useScope } from "@/lib/scope"
import { cn } from "@/lib/utils"
import { GraphCanvas } from "./graph/canvas"
import { Button } from "./ui/button"
import { OpenInGraph } from "./graph/open-in-graph"

const WIDTH = 220
const HEIGHT = 52
const currentEdge = { stroke: "var(--muted-foreground)", strokeWidth: 1.25 } as const

type ObjectFlowNode = Node<{ object: ObjectIdentity; root: boolean; expansion: Expansion }, "object">

function ObjectNode({ data, selected }: NodeProps<ObjectFlowNode>) {
  const t = useT()
  const { object, root, expansion } = data
  const more = expansion.state === "open" && expansion.cursor !== null
  return (
    <div style={{ width: WIDTH, height: HEIGHT }} className={cn("flex items-center gap-2.5 rounded-lg border bg-card px-3 transition-[border-color,box-shadow] duration-150", root ? "border-primary/70" : "border-border", selected && "border-primary shadow-[0_0_0_3px_var(--primary-soft)]")}>
      <Handle type="target" position={Position.Left} className="!opacity-0" />
      <Handle type="source" position={Position.Right} className="!opacity-0" />
      <Shapes className={cn("size-3.5 shrink-0", root ? "text-primary" : "text-subtle-foreground")} aria-hidden />
      <div className="min-w-0 flex-1 leading-tight">
        <div className="truncate font-mono text-xs text-foreground" title={object.key}>
          {object.key}
        </div>
        <div className="truncate text-2xs text-subtle-foreground">{object.objectType}</div>
      </div>
      {expansion.state === "loading" ? <Loader2 className="size-3.5 animate-spin text-subtle-foreground" aria-label={t("neighborhood.loadingLinks")} /> : null}
      {expansion.state === "collapsed" || more ? <Plus className="size-3.5 text-subtle-foreground" aria-label={more ? t("neighborhood.moreLinksMayExist") : t("neighborhood.notExpanded")} /> : null}
    </div>
  )
}

const nodeTypes = { object: ObjectNode }

/**
 * An object's committed links, one hop at a time. Each node expands on demand and follows its own
 * cursor; a short or empty page with a cursor is never treated as the end. Bounded to NODE_BOUND nodes.
 */
export function NeighborhoodPanel({ root }: { root: ObjectIdentity }) {
  const t = useT()
  const scope = useScope()
  const page = useLinkPager(scope)
  const [graph, setGraph] = useState<Neighborhood>(() => start(root))
  const [selected, setSelected] = useState<string>(nodeId(root))

  const expand = async (id: string) => {
    const object = graph.nodes.get(id)
    const current = graph.expansion.get(id)
    if (!object || current?.state === "loading") return
    if (current?.state === "open" && current.cursor === null) return
    const cursor = current?.state === "open" || current?.state === "failed" ? current.cursor : null
    setGraph((g) => setExpansion(g, id, { state: "loading" }))
    try {
      const result = await page(object, cursor)
      setGraph((g) => addPage(g, id, result))
    } catch (error) {
      setGraph((g) => setExpansion(g, id, { state: "failed", error, cursor }))
    }
  }

  const layout = useQuery({
    queryKey: ["layout:neighborhood", nodeId(root), [...graph.nodes.keys()].join(","), graph.edges.size],
    queryFn: () =>
      layeredLayout(
        [...graph.nodes.keys()].map((id) => ({ id, width: WIDTH, height: HEIGHT })),
        [...graph.edges.entries()].map(([id, e]) => ({ id, source: nodeId(e.from), target: nodeId(e.to) })),
      ),
    placeholderData: (prev) => prev,
  })
  const flow = useMemo(() => {
    const positions = layout.data ?? new Map<string, { x: number; y: number }>()
    const nodes: ObjectFlowNode[] = [...graph.nodes.entries()].map(([id, object]) => ({
      id,
      type: "object",
      position: positions.get(id) ?? { x: 0, y: 0 },
      data: { object, root: id === graph.root, expansion: graph.expansion.get(id) ?? { state: "collapsed" } },
    }))
    const edges: Edge[] = [...graph.edges.entries()].map(([id, e]) => ({
      id,
      source: nodeId(e.from),
      target: nodeId(e.to),
      label: e.linkId,
      type: "smoothstep",
      style: currentEdge,
      markerEnd: { type: MarkerType.ArrowClosed, width: 12, height: 12, color: "var(--muted-foreground)" },
    }))
    return { nodes, edges }
  }, [graph, layout.data])

  const sel = graph.nodes.get(selected)
  const selExpansion = graph.expansion.get(selected) ?? { state: "collapsed" }
  const refused = selExpansion.state === "failed" ? refusalOfError(selExpansion.error) : null
  const linksOf = [...graph.edges.values()].filter((e) => nodeId(e.from) === selected || nodeId(e.to) === selected)

  return (
    <div className="flex h-full border-t border-border-subtle">
      <div className="relative min-w-0 flex-1">
        <GraphCanvas nodes={flow.nodes} edges={flow.edges} nodeTypes={nodeTypes} onNodeClick={(n) => setSelected(n.id)} minFitZoom={0.8}>
          <div className="absolute top-3 start-3 z-10 flex items-center gap-2">
            <OpenInGraph object={root} className="rounded-md border border-border bg-card px-2 py-1" />
            <span className="inline-flex h-6 items-center gap-2 rounded-md border border-border-subtle bg-card px-2 text-xs text-muted-foreground">
              <svg width="22" height="6" aria-hidden>
                <line x1="1" y1="3" x2="21" y2="3" style={currentEdge} />
              </svg>
              {t("neighborhood.committedLinks")}
            </span>
            <span className="text-xs text-subtle-foreground tabular">
              {graph.withheld > 0 ? t("neighborhood.withheldLinks", { count: graph.withheld, objectCount: graph.nodes.size, limit: NODE_BOUND }) : t("neighborhood.objectCount", { count: graph.nodes.size })}
            </span>
          </div>
        </GraphCanvas>
      </div>
      <aside className="w-80 shrink-0 overflow-y-auto border-s border-border-subtle bg-sidebar p-4" aria-label={t("neighborhood.selectedObject")}>
        {sel ? (
          <>
            <h2 className="truncate font-mono text-sm">{sel.key}</h2>
            <p className="text-xs text-muted-foreground">{sel.objectType}</p>
            <div className="mt-4 flex flex-col gap-2">
              {selExpansion.state === "open" && selExpansion.cursor === null ? (
                <p className="text-xs text-muted-foreground">{t("neighborhood.allLinksShown", { count: linksOf.length })}</p>
              ) : (
                <Button size="sm" variant="primary" disabled={selExpansion.state === "loading"} onClick={() => void expand(selected)}>
                  {selExpansion.state === "loading" ? <Loader2 className="animate-spin" /> : <Plus />}
                  {selExpansion.state === "collapsed" ? t("neighborhood.showLinks") : selExpansion.state === "failed" ? t("neighborhood.retry") : t("neighborhood.loadMoreLinks")}
                </Button>
              )}
              {selExpansion.state === "open" && selExpansion.cursor !== null ? <p className="text-2xs text-subtle-foreground">{t("neighborhood.moreLinksDescription")}</p> : null}
              {selExpansion.state === "failed" ? (
                <p role="alert" className="text-xs text-destructive">
                  {refused?.refusal.code === "explorer.scan_limit" ? t("neighborhood.scanLimit") : refused ? `${refused.refusal.message}: ${refused.refusal.explanation} (${refused.refusal.code})` : selExpansion.error instanceof Error ? selExpansion.error.message : String(selExpansion.error)}
                </p>
              ) : null}
              <Button size="sm" asChild>
                <Link to="/$orgId/$environmentId/objects/$objectType/$key" params={{ ...scope, objectType: sel.objectType, key: sel.key }}>
                  {t("objects.openObject")} <ArrowUpRight className="rtl-mirror" />
                </Link>
              </Button>
            </div>
            {linksOf.length > 0 ? (
              <ul className="mt-4 divide-y divide-border-subtle rounded-md border border-border-subtle text-xs">
                {linksOf.map((e) => {
                  const other = nodeId(e.from) === selected ? e.to : e.from
                  return (
                    <li key={`${nodeId(e.from)}|${e.linkId}|${nodeId(e.to)}`} className="flex items-center gap-2 px-2.5 py-1.5">
                      <code className="font-mono text-2xs text-muted-foreground">{nodeId(e.from) === selected ? `${e.linkId} →` : `← ${e.linkId}`}</code>
                      <button type="button" className="truncate font-mono text-2xs hover:text-primary" onClick={() => setSelected(nodeId(other))}>
                        {other.key}
                      </button>
                    </li>
                  )
                })}
              </ul>
            ) : null}
          </>
        ) : null}
      </aside>
    </div>
  )
}
